import { Button } from "@babackapp/ui/components/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import { Label } from "@babackapp/ui/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@babackapp/ui/components/select";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Earth, FileText, Layers, Mail } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { type TrackingStatusKey, useCreateTemplateFromModel } from "../hooks/use-create-template-from-model";
import { useCreateTemplate } from "../hooks/use-create-template";
import { TEMPLATE_CATEGORY_IDS, useCategoryLabel } from "../lib/categories";
import type { TemplateType } from "../types";


type Step =
  | "type-selection"
  | "email-mode"
  | "web-mode"
  | "email-model-selection"
  | "from-scratch-form";

type CreateTemplateModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTemplateCreated?: (templateId: string) => void;
};


export function CreateTemplateModal({
  open,
  onOpenChange,
  onTemplateCreated,
}: CreateTemplateModalProps) {
  const { t } = useTranslation("trackoffice");
  const [step, setStep] = useState<Step>("type-selection");
  const [selectedType, setSelectedType] = useState<TemplateType | null>(null);
  const [direction, setDirection] = useState<1 | -1>(1);

  // Form state for "from scratch"
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<string | null>(null);

  const { createTemplate, isLoading: isCreatingFromScratch } =
    useCreateTemplate();
  const {
    createFromModel,
    createWebTemplate,
    templateModels,
    isLoading: isCreatingFromModel,
  } = useCreateTemplateFromModel();

  const isLoading = isCreatingFromScratch || isCreatingFromModel;

  const resetState = () => {
    setStep("type-selection");
    setSelectedType(null);
    setDirection(1);
    setTitle("");
    setCategory(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetState();
    }
    onOpenChange(open);
  };

  const goTo = (nextStep: Step, dir: 1 | -1 = 1) => {
    setDirection(dir);
    setStep(nextStep);
  };

  const goBack = () => {
    setDirection(-1);
    switch (step) {
      case "email-mode":
      case "web-mode":
        setStep("type-selection");
        setSelectedType(null);
        break;
      case "email-model-selection":
        setStep("email-mode");
        break;
      case "from-scratch-form":
        setStep(selectedType === "email" ? "email-mode" : "web-mode");
        break;
    }
  };

  const handleTypeSelect = (type: TemplateType) => {
    setSelectedType(type);
    goTo(type === "email" ? "email-mode" : "web-mode");
  };

  const handleCreateFromScratch = async () => {
    if (!selectedType || !title.trim()) return;

    const template = await createTemplate({
      type: selectedType,
      title: title.trim(),
      category: category ?? undefined,
    });

    if (template?.id) {
      onTemplateCreated?.(template.id);
      handleOpenChange(false);
    }
  };

  const handleCreateFromModel = async (modelKey: TrackingStatusKey) => {
    const template = await createFromModel(modelKey, "email");
    if (template?.id) {
      onTemplateCreated?.(template.id);
      handleOpenChange(false);
    }
  };

  const handleCreateWebFromExample = async () => {
    const template = await createWebTemplate();
    if (template?.id) {
      onTemplateCreated?.(template.id);
      handleOpenChange(false);
    }
  };

  const showBackButton = step !== "type-selection";

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? -100 : 100,
      opacity: 0,
    }),
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {showBackButton && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={goBack}
                disabled={isLoading}
              >
                <ArrowLeft className="size-4" />
              </Button>
            )}
            <DialogTitle>
              {t(
                "templatesEditor.createModal.title",
                "Create a new template",
              )}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="overflow-hidden">
          <AnimatePresence mode="wait" custom={direction} initial={false}>
            <motion.div
              key={step}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              {step === "type-selection" && (
                <TypeSelection onSelect={handleTypeSelect} />
              )}

              {step === "email-mode" && (
                <ModeSelection
                  type="email"
                  onFromScratch={() => goTo("from-scratch-form")}
                  onFromModel={() => goTo("email-model-selection")}
                />
              )}

              {step === "web-mode" && (
                <ModeSelection
                  type="web"
                  onFromScratch={() => goTo("from-scratch-form")}
                  onFromExample={handleCreateWebFromExample}
                  isLoading={isLoading}
                />
              )}

              {step === "email-model-selection" && (
                <ModelSelection
                  models={templateModels}
                  onSelect={handleCreateFromModel}
                  isLoading={isLoading}
                />
              )}

              {step === "from-scratch-form" && (
                <FromScratchForm
                  type={selectedType!}
                  title={title}
                  setTitle={setTitle}
                  category={category}
                  setCategory={setCategory}
                  onCreate={handleCreateFromScratch}
                  isLoading={isLoading}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function TypeSelection({
  onSelect,
}: {
  onSelect: (type: TemplateType) => void;
}) {
  const { t } = useTranslation("trackoffice");

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          "templatesEditor.createModal.typeSelection.description",
          "What type of template do you want to create?",
        )}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <OptionCard
          icon={<Mail className="size-8" />}
          title={t("templatesEditor.createModal.typeSelection.email", "Email")}
          description={t(
            "templatesEditor.createModal.typeSelection.emailDescription",
            "Notification emails sent to your customers",
          )}
          onClick={() => onSelect("email")}
        />
        <OptionCard
          icon={<Earth className="size-8" />}
          title={t("templatesEditor.createModal.typeSelection.web", "Web")}
          description={t(
            "templatesEditor.createModal.typeSelection.webDescription",
            "Tracking page displayed in the browser",
          )}
          onClick={() => onSelect("web")}
        />
      </div>
    </div>
  );
}


function ModeSelection({
  type,
  onFromScratch,
  onFromModel,
  onFromExample,
  isLoading,
}: {
  type: TemplateType;
  onFromScratch: () => void;
  onFromModel?: () => void;
  onFromExample?: () => void;
  isLoading?: boolean;
}) {
  const { t } = useTranslation("trackoffice");

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          "templatesEditor.createModal.modeSelection.description",
          "How do you want to start?",
        )}
      </p>
      <div className="grid grid-cols-2 gap-4">
        <OptionCard
          icon={<FileText className="size-8" />}
          title={t(
            "templatesEditor.createModal.modeSelection.fromScratch",
            "From scratch",
          )}
          description={t(
            "templatesEditor.createModal.modeSelection.fromScratchDescription",
            "Start with an empty template",
          )}
          onClick={onFromScratch}
          disabled={isLoading}
        />
        {type === "email" && onFromModel && (
          <OptionCard
            icon={<Layers className="size-8" />}
            title={t(
              "templatesEditor.createModal.modeSelection.fromModel",
              "From a model",
            )}
            description={t(
              "templatesEditor.createModal.modeSelection.fromModelDescription",
              "Use a pre-designed template",
            )}
            onClick={onFromModel}
            disabled={isLoading}
          />
        )}
        {type === "web" && onFromExample && (
          <OptionCard
            icon={<Layers className="size-8" />}
            title={t(
              "templatesEditor.createModal.modeSelection.fromExample",
              "From an example",
            )}
            description={t(
              "templatesEditor.createModal.modeSelection.fromExampleDescription",
              "Use a pre-configured tracking page",
            )}
            onClick={onFromExample}
            disabled={isLoading}
          />
        )}
      </div>
    </div>
  );
}


type ModelWithCategory = {
  type: string;
  title?: string;
  subject?: Record<string, unknown>;
  category?: string | null;
};


function ModelSelection({
  models,
  onSelect,
  isLoading,
}: {
  models: [TrackingStatusKey, ModelWithCategory][];
  onSelect: (modelKey: TrackingStatusKey) => void;
  isLoading?: boolean;
}) {
  const { t } = useTranslation("trackoffice");
  const getCategoryLabel = useCategoryLabel();

  // Group models by category and sort by title
  const modelsByCategory = models.reduce(
    (acc, [key, template]) => {
      const categoryId = template.category || "others";
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push([key, template] as [TrackingStatusKey, ModelWithCategory]);
      return acc;
    },
    {} as Record<string, [TrackingStatusKey, ModelWithCategory][]>,
  );

  // Sort models within each category by title
  for (const categoryId of Object.keys(modelsByCategory)) {
    modelsByCategory[categoryId].sort((a, b) => {
      const titleA = a[1].title || a[1].type;
      const titleB = b[1].title || b[1].type;
      return titleA.localeCompare(titleB);
    });
  }

  // Order categories according to TEMPLATE_CATEGORY_IDS
  const orderedCategories = TEMPLATE_CATEGORY_IDS.filter(
    (catId) => modelsByCategory[catId]?.length > 0,
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          "templatesEditor.createModal.modelSelection.description",
          "Choose a template model",
        )}
      </p>
      <div className="max-h-[320px] overflow-y-auto pr-2 space-y-4">
        {orderedCategories.map((categoryId) => (
          <div key={categoryId}>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              {getCategoryLabel(categoryId)}
            </p>
            <div className="flex flex-col gap-1">
              {modelsByCategory[categoryId].map(([key, template]) => (
                <Button
                  key={String(key)}
                  variant="ghost"
                  className="justify-start h-auto py-2 px-3 text-left hover:bg-accent flex-col items-start gap-0.5"
                  onClick={() => onSelect(key)}
                  disabled={isLoading}
                >
                  <span>{template.title || template.type}</span>
                  <span className="text-[8px] text-muted-foreground font-normal">
                    {template.type.toUpperCase()}
                  </span>
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function FromScratchForm({
  type,
  title,
  setTitle,
  category,
  setCategory,
  onCreate,
  isLoading,
}: {
  type: TemplateType;
  title: string;
  setTitle: (value: string) => void;
  category: string | null;
  setCategory: (value: string | null) => void;
  onCreate: () => void;
  isLoading?: boolean;
}) {
  const { t } = useTranslation("trackoffice");
  const getCategoryLabel = useCategoryLabel();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate();
  };

  const translatedType = t(
    `templatesEditor.createModal.types.${type}`,
    type,
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          "templatesEditor.createModal.fromScratchForm.description",
          "Configure your new {type} template",
          { type: translatedType },
        )}
      </p>

      <div className="space-y-2">
        <Label htmlFor="template-title">
          {t("templatesEditor.createModal.fromScratchForm.titleLabel", "Title")}
        </Label>
        <Input
          id="template-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t(
            "templatesEditor.createModal.fromScratchForm.titlePlaceholder",
            "My new template",
          )}
          autoFocus
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="template-category">
          {t(
            "templatesEditor.createModal.fromScratchForm.categoryLabel",
            "Category",
          )}
        </Label>
        <Select
          value={category ?? "none"}
          onValueChange={(value) =>
            setCategory(value === "none" ? null : value)
          }
        >
          <SelectTrigger id="template-category">
            <SelectValue
              placeholder={t(
                "templatesEditor.createModal.fromScratchForm.categoryPlaceholder",
                "Select a category",
              )}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">
              {t(
                "templatesEditor.createModal.fromScratchForm.noCategory",
                "No category",
              )}
            </SelectItem>
            {TEMPLATE_CATEGORY_IDS.map((catId) => (
              <SelectItem key={catId} value={catId}>
                {getCategoryLabel(catId)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={!title.trim() || isLoading}>
          {t(
            "templatesEditor.createModal.fromScratchForm.createButton",
            "Create template",
          )}
        </Button>
      </div>
    </form>
  );
}


function OptionCard({
  icon,
  title,
  description,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-3 p-6 border rounded-lg hover:bg-accent hover:border-primary/50 transition-colors text-center disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="text-muted-foreground">{icon}</div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </div>
    </button>
  );
}
